import { flowTypeUsesInlineWaits } from '@chatsdet/types';

/**
 * Runtime-only keys that exist on node.data in React state but must never be
 * sent to the backend as part of configJson. Maintained in ONE place so
 * NodeConfigPanel and useNodeCrud stay in sync.
 */
const RUNTIME_NODE_KEYS = new Set([
  // Event handlers injected by hydration / useNodeCrud
  'onConfigClick',
  'onCreateNavigate',
  'onCreateAction',
  'onCreateAssertion',
  'onCreateWait',
  'onCreateLogin',
  'onCreateCondition',
  'onCreateYesBranch',
  'onCreateNoBranch',
  'onCacheClick',
  'onViewFlow',
  'onPlaybookClick',
  'playbookStepCount',
  'inlineAssertionCount',
  // Execution state (set during runs)
  'executionStatus',
  'executionMessage',
  'isExecuting',
  // Persistence marker
  'isPersisted',
  // Derived at render time from credentialId + org credentials cache
  'credentialName',
  'propertyName',
  // Canvas-only masking metadata derived from the action schema
  'sensitiveParams',
  // Condition node runtime state
  'conditionResult',
  // Display-only step labels for API sequence canvas cards
  '_stepLabels',
  // Cache status (injected by workflow detail response, not part of configJson)
  'hasCachedReplay',
  'cachedAt',
  'cacheRequiresAgent',
  // Setup flow preview data (injected by workflow detail response)
  'setupFlowPreview',
  // Feature flag (injected at hydration time)
  'enableApiSequence',
]);

/**
 * Strip runtime-only keys and functions from node data, returning only the
 * persistable configJson fields. Used before sending to the backend.
 */
export function sanitizeNodeDataForPersistence(
  data?: Record<string, any> | null
): Record<string, any> {
  if (!data) return {};
  return Object.fromEntries(
    Object.entries(data).filter(
      ([key, value]) => typeof value !== 'function' && !RUNTIME_NODE_KEYS.has(key)
    )
  );
}

export const getDefaultNodeData = (type: string, flowType?: string): Record<string, any> => {
  switch (type) {
    case 'wait':
      // Seed/teardown flows run waits inline and cap them, so the standard
      // 30-minute default would be invalid the moment the node is created.
      return { mode: 'delay', delaySeconds: flowTypeUsesInlineWaits(flowType) ? 60 : 1800 };
    case 'visual_checkpoint':
      return {
        name: 'Checkpoint',
        severity: 'record',
        title: 'Visual checkpoint: Checkpoint',
        description: 'Visual checkpoint: Checkpoint',
      };
    default:
      return {};
  }
};

export const isTempNodeId = (id: string) =>
  /^(login|navigate|action|assertion|wait|condition|api_sequence|sandbox_action|mcp|setup|end|temp-prepend|temp-paste)-\d+$/.test(
    id
  );

export const formatUpdatedAt = (value?: string | null) => {
  if (!value) return 'Not yet updated';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};
